import { useState } from "react";

import {
  blockedUserCount,
  metricPath,
  metricValue,
  tenantsOf,
  type FleetMetricEnvelope,
  type McpTenantList,
} from "../../api/mcp";
import { ResourceView, statOf } from "../../components/states";
import { Notice, Page, Stat, StatRow } from "../../components/ui";
import type { AppConfig } from "../../config";
import { useAuditFeed } from "../../hooks/useAuditFeed";
import { useResource } from "../../hooks/useResource";
import { useTenantDetails } from "../../hooks/useTenantDetails";
import { ProductScope, ProductSection } from "../../products/PerProduct";
import { McpAccess } from "./access/McpAccess";
import { LabeebSuspendLookup } from "./access/LabeebSuspendLookup";

/**
 * Who is blocked, at what scope.
 *
 * Three scopes exist and they are NOT interchangeable — the whole value of
 * this page is keeping them apart:
 *
 *   merchant-wide      ("<ref>", "*")   nobody at this merchant
 *   user at a merchant ("<ref>", email) one person, one merchant
 *   user everywhere    ("*", email)     one person, every merchant
 *
 * Unblocking at one scope never lifts another, so an operator who cannot see
 * which scope applies will try the wrong lever and conclude the API is broken.
 */
export function Access({ config }: { config: AppConfig }) {
  const { state, reload } = useResource<McpTenantList>(config, "/mcp/admin/tenants");
  // The fleet-wide count, from its own metric rather than summed client-side —
  // the list this page reads is capped to merchants seen in the last year, so
  // a client-side sum would silently undercount against the server's own
  // figure whenever a dormant, blocked merchant fell outside that window.
  const blockedMetric = useResource<FleetMetricEnvelope<"blocked">>(
    config,
    metricPath("blocked"),
  );
  // Who blocked whom, when, and why — absent from every table on this page
  // otherwise, since a blocklist row is deleted on unblock and carries no
  // history of its own.
  const blockAudit = useAuditFeed(config, "tenant");
  // Null until there is a real pair of numbers — see `metricValue`.
  const blockedCounts = metricValue(blockedMetric.state);
  // Which scope the metric row has narrowed to. The three scopes are the
  // whole point of this page, so the filter is BY SCOPE rather than by a
  // search string — "show me the merchant-wide blocks" is the question.
  const [scope, setScope] = useState<"merchants" | "users" | null>(null);
  const [email, setEmail] = useState("");
  const [ref, setRef] = useState("");
  const [blockEmail, setBlockEmail] = useState("");

  // Only the merchants that actually need a fan-out: a small, bounded set
  // (blocked_users_count > 0) that the list response already lacks names for.
  // A merchant still carrying `blocked_users` from the list needs no second
  // call. Computed here, unconditionally, so the hook itself is never called
  // conditionally on `state.kind`.
  // Merchant-wide blocks are fanned out too — the list carries `blocked`
  // itself, but not `blocked_reason`/`blocked_by`/`blocked_since`, and those
  // three are exactly what an operator needs to know before touching the
  // block.
  const needsFanOut =
    state.kind === "ready"
      ? [
          ...new Set([
            ...tenantsOf(state.data)
              .filter((t) => blockedUserCount(t) > 0 && !t.blocked_users)
              .map((t) => t.business_reference),
            ...tenantsOf(state.data).filter((t) => t.blocked).map((t) => t.business_reference),
          ]),
        ]
      : [];
  const { byRef: fannedOutDetails } = useTenantDetails(config, needsFanOut);

  // Trimmed once, so the enable check and the URL cannot drift apart. Both of
  // these arrive by paste — from a spreadsheet, or out of Slack — and a
  // trailing space is the normal case rather than the odd one.
  const checkRef = ref.trim();
  const checkEmail = email.trim();

  return (
    <Page title="Access" subtitle="Who is refused, and at what scope.">

      {/* TWO MECHANISMS, TWO PRODUCTS, and BOTH are here now — Labeeb's
          suspension route shipped, so the old "is not wired" line was false.
          The warning it carried still matters and gets sharper: these do not
          substitute for each other. A block is MCP's blocklist row and stops
          tool calls; a suspension is Labeeb's and stops the assistant
          answering at all. An operator who blocks a merchant here and assumes
          they have been cut off has cut off half of it. */}
      <ProductScope owns={["mcp", "labeeb"]}>
        a block stops MCP tool calls; a suspension stops Labeeb answering — doing one does not
        do the other
      </ProductScope>

      {/* The fleet-wide figures, from their own metric — not summed from the
          tables below, which only cover merchants seen in the last year.

          Each is a FILTER: clicking "2 merchants blocked" narrows the page to
          those two, which is how an operator gets from the count to the rows
          without scanning. Clicking it again clears. */}
      <StatRow>
        <Stat
          {...statOf(blockedMetric.state, (env) => env.value?.merchants)}
          label="Merchants blocked"
          tone={blockedCounts && blockedCounts.merchants > 0 ? "amber" : "neutral"}
          onClick={() => setScope(scope === "merchants" ? null : "merchants")}
          active={scope === "merchants"}
        />
        <Stat
          hint="one person, every merchant"
          {...statOf(blockedMetric.state, (env) => env.value?.users_everywhere)}
          label="Users blocked everywhere"
          tone={blockedCounts && blockedCounts.users_everywhere > 0 ? "amber" : "neutral"}
          onClick={() => setScope(scope === "users" ? null : "users")}
          active={scope === "users"}
        />
      </StatRow>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        From <span className="ref">GET /admin/metrics/blocked</span>, independent of the
        merchant list below.
      </p>

      <ResourceView state={state} what="the block list" onRetry={reload} />

      {state.kind === "ready" &&
        (() => {
          const d = state.data;
          const list = tenantsOf(d);
          const blockedMerchants = list.filter((t) => t.blocked);
          const withUserBlocks = list.filter((t) => blockedUserCount(t) > 0);
          const everywhere = d.users_blocked_across_all_merchants ?? [];
          const nothing =
            blockedMerchants.length === 0 && withUserBlocks.length === 0 && everywhere.length === 0;

          return (
            <>
              {nothing && (
                <Notice title="Nobody is blocked">
                  No merchant-wide blocks, no per-user blocks, and nobody blocked across all
                  merchants.
                </Notice>
              )}

              {/* ONE COLLAPSIBLE BLOCK PER PRODUCT, the same shape as Policy
                  -> Settings. This page used to be seven flat MCP cards with
                  Labeeb's single card last, which read as "Access is an MCP
                  page that happens to mention Labeeb" — and buried the one
                  control that stops the assistant answering at all under six
                  panels about tool calls.
                  Two blocks, same shape, both open by default: a block per
                  product says the two mechanisms are peers, which is the fact
                  an operator most needs here. Blocking in MCP does NOT stop
                  the assistant, and suspending in Labeeb does NOT stop tool
                  calls. */}
              <McpAccess
                config={config}
                scope={scope}
                everywhere={everywhere}
                blockedMerchants={blockedMerchants}
                withUserBlocks={withUserBlocks}
                fannedOutDetails={fannedOutDetails}
                reload={reload}
                blockAudit={blockAudit}
                blockEmail={blockEmail}
                setBlockEmail={setBlockEmail}
                merchantRef={ref}
                setMerchantRef={setRef}
                email={email}
                setEmail={setEmail}
                checkRef={checkRef}
                checkEmail={checkEmail}
              />

              {/* NO LONGER BLURRED. `GET`/`PATCH /admin/tenant/{id}/standing`
                  exists, so the design's second layer is real.
                  It stays a PER-MERCHANT lookup rather than a fleet table:
                  Labeeb serves no fleet-wide list of suspensions, and a table
                  built by fetching standing for every merchant would be N
                  requests to answer a question this page can answer for the
                  one merchant an operator is actually looking at.
                  The same control is on /tenants/{ref} — one write, two
                  doors, matching how MCP blocking already works here. */}
            </>
          );
        })()}

      {/* OUTSIDE THE MCP BRANCH, DELIBERATELY.
          Labeeb's suspend control reads nothing from MCP — it is a
          per-merchant lookup against Labeeb's own standing routes. Nested
          inside the MCP-ready branch it disappeared whenever MCP was
          unreachable, taking Labeeb's enforcement lever off the page because
          a DIFFERENT product was down. That is the lever an operator reaches
          for during an incident, so it must not depend on MCP being healthy. */}
      <ProductSection id="labeeb" note="Who is refused the assistant entirely">
        <LabeebSuspendLookup config={config} />
      </ProductSection>
    </Page>
  );
}
